import { FC } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, TextField, Stack } from "@mui/material";
import __ from "src/utils/Translations";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useRequireCorrectionAbsenceRequest } from "src/Pages/Company/Absences/api/absenceRequests.queries";
import { IAbsenceRequestRequireCorrectionFormValues } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { requireCorrectionSchema } from "./AbsenceRequestRequireCorrectionForm.schema";

interface IAbsenceRequestRequireCorrectionFormProps {
  absenceRequestId: string;
  eTag: string;
  resetModal: VoidFunction;
}

const AbsenceRequestRequireCorrectionForm: FC<
  IAbsenceRequestRequireCorrectionFormProps
> = ({ absenceRequestId, eTag, resetModal }) => {
  const logStatus = useLogStatus();

  if (!absenceRequestId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Request ID provided.",
      file: "AbsenceRequestRequireCorrectionForm.tsx",
    });
  }

  const { mutate: requireCorrectionAbsenceRequest, isPending } =
    useRequireCorrectionAbsenceRequest(absenceRequestId, resetModal);

  const onSubmit = (formData: IAbsenceRequestRequireCorrectionFormValues) => {
    requireCorrectionAbsenceRequest({ data: formData, eTag });
  };

  const formMethods = useForm<IAbsenceRequestRequireCorrectionFormValues>({
    resolver: zodResolver(requireCorrectionSchema),
    defaultValues: {
      note: "",
    },
  });

  const { control, handleSubmit } = formMethods;

  return (
    <FormProvider {...formMethods}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="note"
          control={control}
          render={({ field, fieldState: { invalid, error } }) => (
            <TextField
              {...field}
              id="note"
              label={__("Podaj uzasadnienie")}
              inputRef={field.ref}
              error={invalid}
              helperText={error?.message}
              rows={6}
              multiline
              fullWidth
              required
            />
          )}
        />
        <Stack
          direction="row"
          sx={{ justifyContent: "flex-end", mt: "2rem" }}
          spacing={2}
        >
          <Button
            variant="outlined"
            size="large"
            onClick={resetModal}
            disabled={isPending}
          >
            {__("Anuluj")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            loading={isPending}
            disabled={isPending}
            loadingPosition="start"
          >
            {__("Wyślij")}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  );
};

export default AbsenceRequestRequireCorrectionForm;
